import { Trans, useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { H1, QuickActionButton } from '@/components/atoms'
import { ChevronRightIcon } from '@/components/atoms/icons'
import {
  FilterChips,
  GoalCard,
  IdCard,
  PerkCard,
  PromoCard,
  ReferModal,
} from '@/components/molecules'
import { ROUTES } from '@/shared/constants/routes'
import { useHomePage } from './page.home.hook'
import { useHomeMobile } from './page.home.mobile.hook'

const SCROLLER_CLASS = 'scrollbar-none -mx-4 flex overflow-x-auto px-4'

const SECTION_TITLE_CLASS =
  'tracking-base text-base leading-4 font-bold text-neutral-900'

export function HomeMobile() {
  const { t } = useTranslation()
  const {
    firstName,
    quickActions,
    showRefer,
    isReferModalOpen,
    openReferModal,
    closeReferModal,
  } = useHomePage()
  const {
    policyFilters,
    policyFilter,
    setPolicyFilter,
    policies,
    policyLink,
    showGoals,
    goalColumns,
    showPerks,
    perks,
  } = useHomeMobile()

  return (
    <div className="flex flex-col gap-6" data-testid="home-page">
      <H1
        className="text-[2.625rem]! leading-12.5! font-semibold text-neutral-900"
        data-testid="home-greeting"
      >
        {firstName ? t('home.greeting', { name: firstName }) : ''}
      </H1>

      <section className="flex flex-col gap-6" data-testid="home-policies">
        <div className="flex flex-col gap-2">
          <h2 className={SECTION_TITLE_CLASS}>{t('home.policies.title')}</h2>
          <FilterChips
            filters={policyFilters}
            active={policyFilter}
            onSelect={setPolicyFilter}
          />
        </div>
        <div className={`${SCROLLER_CLASS} gap-2`}>
          {policies.map((policy) => (
            <IdCard
              key={policy.id}
              variant="with-photo"
              isMobile
              className="w-80.75 shrink-0"
              photoUrl={policy.photoUrl}
              hidePhotoBackground
              planName={policy.name}
              ownerName={policy.policyNumber}
              link={policyLink}
            />
          ))}
        </div>
      </section>

      {quickActions.length > 0 && (
        <nav
          aria-label={t('home.quickActions.ariaLabel')}
          className={`${SCROLLER_CLASS} gap-2`}
        >
          {quickActions.map((action) => (
            <QuickActionButton
              key={action.to}
              icon={action.icon}
              label={action.label}
              to={action.to}
              testId={action.testId}
              className="w-full max-w-28.25 shrink-0"
            />
          ))}
        </nav>
      )}

      {showGoals && (
        <section className="flex flex-col gap-4" data-testid="home-goals">
          <div className="flex items-center gap-3">
            <h2 className={SECTION_TITLE_CLASS}>{t('home.goals.title')}</h2>
          </div>
          <div className="grid grid-cols-2 items-stretch gap-4">
            {goalColumns.map((column, index) => (
              <div key={index} className="flex flex-col justify-between gap-4">
                {column.map((goal) => (
                  <GoalCard
                    key={goal.id}
                    icon={goal.icon}
                    description={
                      <Trans i18nKey={goal.i18nKey} values={goal.values} />
                    }
                    layout={goal.layout}
                    size={goal.size}
                    link={goal.link}
                    testId={`home-goal-${goal.id}`}
                  />
                ))}
              </div>
            ))}
          </div>
        </section>
      )}

      {showPerks && (
        <section className="flex flex-col gap-4" data-testid="home-perks">
          <div className="flex items-center justify-between">
            <h2 className={SECTION_TITLE_CLASS}>{t('home.perks.title')}</h2>
            <Link
              to={ROUTES.PERKS}
              className="tracking-base text-indigo-base flex h-8 items-center gap-1 text-xs leading-4 hover:underline"
              data-testid="home-perks-see-all"
            >
              {t('home.perks.showDetails')}
              <ChevronRightIcon className="size-3" />
            </Link>
          </div>
          <div className={`${SCROLLER_CLASS} gap-4`}>
            {perks.map((perk) => (
              <PerkCard
                key={perk.id}
                image={perk.image}
                title={perk.title}
                description={<Trans i18nKey={perk.i18nKey} />}
                accent={perk.accent}
                action={perk.action}
                className="w-50 shrink-0"
                testId={`home-perk-${perk.id}`}
              />
            ))}
          </div>
        </section>
      )}

      {showRefer && (
        <PromoCard
          variant="coral"
          title={t('home.refer.title')}
          titleHighlight={t('home.refer.titleHighlight')}
          description={<Trans i18nKey="home.refer.description" />}
          action={{ label: t('home.refer.cta'), onClick: openReferModal }}
          testId="home-refer-card"
        />
      )}

      {showRefer && (
        <ReferModal isOpen={isReferModalOpen} onClose={closeReferModal} />
      )}
    </div>
  )
}
